import * as XTypeChecks from './XTypeChecks.js';

export function xStringify(any)
{
    if(any instanceof Object && 'valueOf' in any)
    {
        return JSON.stringify(any.valueOf());
    }
    return JSON.stringify(any);
}

export function xCheckSystem_iOS()
{
    return [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform)
    // iPad on iOS 13 detection
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
}

export function xGetScaledResolutions(input, targets, upscale=false) 
{
    const resolutions = targets.map(target => 
    {
        let scale = Math.min(
            target.width  / input.width,
            target.height / input.height
        );
        if(scale > 1.0 && !upscale)
        {
            scale = 1.0;
        }

        const scaledWidth  = Math.ceil(input.width  * scale);
        const scaledHeight = Math.ceil(input.height * scale);

        return {
            width: scaledWidth,
            height: scaledHeight
        };
    });
    const seen = new Set();
    return resolutions.filter(({width,height}) => {
        const key = `${width}x${height}`;
        if(seen.has(key))
        {
            return false;
        }
        seen.add(key);
        return true;
    });
}

export async function xHashString(input) 
{
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-1', data); // or 'SHA-256'
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
}

export async function xGenerateUUIDv4() 
{
    const bytes = crypto.getRandomValues(new Uint8Array(16));

    // Per RFC 4122:
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10

    return [...bytes].map((b, i) => {
        const hex = b.toString(16).padStart(2, '0');
        return [4, 6, 8, 10].includes(i) ? `-${hex}` : hex;
    }).join('');
}

export function xSetHiddenField(object, name, value)
{
    Object.defineProperty(object, name, {
        value: value,
        enumerable: false,
        configurable: true,
        writable: false
    });
}

export async function xRunSequentialPromises(promiseFunctions) 
{
    const results = [];
    for (const promiseFunction of promiseFunctions) 
    {
        const result = await promiseFunction();
        results.push(result);
    }
    return results;
}

  
export function xObjectToBase64(object) 
{
    if(!object)
    {
        return '';
    }
    const utf8Bytes = new TextEncoder().encode(JSON.stringify(object));
    return btoa(String.fromCharCode(...utf8Bytes));
}

export function xBase64ToBuffer(base64String) 
{
    if(!base64String)
    {
        return null;
    }
    return Uint8Array.from(atob(base64String), char => char.charCodeAt(0));
}

export function xBase64ToObject(base64String) 
{
    if(!base64String)
    {
        return {};
    }
    const utf8Bytes = Uint8Array.from(atob(base64String), char => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(utf8Bytes));
}

export function xRandomString(length=9, characters='abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789')
{
    let result = '';
    for(let i = 0; i < length; i++) 
    {
        result += characters[Math.floor(Math.random() * characters.length)];
    }
    return result;
}

export function xReplaceVariables(string, variables) 
{
    for(const name in variables)
    {
        string = string.replaceAll('${' + name + '}', variables[name]);
    }
    return string;
}

export function xClearObject(object, ...keepKeys) 
{
    for(const key of Object.getOwnPropertyNames(object)) 
    {
        if(keepKeys.includes(key))
        {
            continue;
        }
        delete object[key];
    }
}

export function xDeepCopy(object) 
{
    if(object === null || typeof object !== 'object') 
    {
        return object;
    }
    if(Array.isArray(object)) 
    {
        return object.map(item => xDeepCopy(item));
    }
    const copy = {};
    for(const key in object) 
    {
        if(object.hasOwnProperty(key)) 
        {
            copy[key] = xDeepCopy(object[key]);
        }
    }
    return copy;
}

export function xDeepMerge(target, source) 
{
    if(source === null || typeof source !== 'object') 
    {
        return source;
    }
    if(Array.isArray(source)) 
    {
        target.length = 0;
        source.forEach(item => target.push(xDeepCopy(item)));
        return target;
    }
    for(const key of Object.getOwnPropertyNames(source)) 
    {
        if(typeof source[key] === 'object' && source[key] !== null)
        {
            if(!target[key] || typeof target[key] !== 'object')
            {
                target[key] = Array.isArray(source[key]) ? [] : {};
            }
            xDeepMerge(target[key], source[key]);
        } 
        else 
        {
            target[key] = source[key];
        }
    }
    return target;
}

export function xMerge(target, source) 
{
    for(const key of Object.getOwnPropertyNames(source)) 
    {
        target[key] = source[key];
    }
    return target;
}

export function xMergeFill(target, source) 
{
    for(const key of Object.getOwnPropertyNames(source)) 
    {
        if(!(key in target))
        {
            target[key] = source[key];
        }
    }
    return target;
}

export function xChangeElementType(element, newTagName)
{
    const newElement = document.createElement(newTagName);

    for(const attribute of element.attributes) 
    {
        newElement.setAttribute(attribute.name, attribute.value);
    }

    newElement.append(...Array.from(element.children));
    element.replaceWith(newElement);
    return newElement;
}

export function xExtractAriaTextFromHTML(htmlText) 
{
    const container = document.createElement('div');
    container.innerHTML = htmlText;

    function xExtract(node)
    {
        if(node.nodeType === Node.TEXT_NODE)
        {
            return node.nodeValue;
        }
        if(node.nodeType === Node.ELEMENT_NODE)
        {
            if (node.hasAttribute('aria-label'))
            {
                return node.getAttribute('aria-label');
            }
            return Array.from(node.childNodes).map(xExtract).join('');
        }
        return '';
    }
    const rawText = Array.from(container.childNodes).map(xExtract).join('');
    return rawText.replace(/\s+/g, ' ').trim();
}

export function xExtractTextWithTokensFromHTML(htmlText) 
{
    const container = document.createElement('div');
    container.innerHTML = htmlText;

    let index = 0;
    let tokens = {};
    function xExtract(node)
    {
        if(node.nodeType === Node.TEXT_NODE)
        {
            return node.nodeValue;
        }
        if(node.nodeType === Node.ELEMENT_NODE)
        {
            const token = 'X' + String(index);
            index += 1;
            let outerNode = node.cloneNode();
            outerNode.innerHTML = '';
            outerNode = outerNode.outerHTML.split('></');
            outerNode[0] = outerNode[0] + '>';
            outerNode[1] = '</' + outerNode[1];
            tokens[token] = outerNode;
            const rawInnerText = Array.from(node.childNodes).map(xExtract).join('');
            return '<' + token + '>' + rawInnerText + '</' + token + '>';
        }
        return '';
    }
    const rawText = Array.from(container.childNodes).map(xExtract).join('');
    return {
        tokens: tokens,
        text: rawText.replace(/\s+/g, ' ').trim()
    }
}

export function xComposeTextWithTokensToHTML(textWithTokens) 
{
    let text = textWithTokens.text;
    const tokens = textWithTokens.tokens;
    for(let token in tokens)
    {
        const outerNode = tokens[token];
        const tokenStart = '<'  + token + '>';
        const tokenEnd   = '</' + token + '>';
        if(text.indexOf(tokenStart) < 0)
        {
            console.warn('Token ' + tokenStart + ' was not found in text!', textWithTokens.text);
        }
        else
        {
            text = text.replace(tokenStart, outerNode[0]);
        }
        if(text.indexOf(tokenEnd) < 0)
        {
            console.warn('Token ' + tokenEnd + ' was not found in text!', textWithTokens.text);
        }
        else
        {
            text = text.replace(tokenEnd, outerNode[1]);
        }
    }
    return text;
}

export function xGetFirstChildWithClass(element, classname)
{
    for(let elementChild of element.children)
    {
        if(elementChild.classList.contains(classname))
        {
            return elementChild;
        }
    }
    return null;
};
export function xGetFirstChildOfType(element, typename)
{
    for(let elementChild of element.children)
    {
        if(elementChild.tagName.toLowerCase() == typename)
        {
            return elementChild;
        }
    }
    return null;
};
export function xGetStringFromDatasetAndRemove(element, variable, defaultValue)
{
    if(variable in element.dataset)
    {
        const value = element.dataset[variable];
        delete element.dataset[variable];
        return value;
    }
    return defaultValue;
};
export function xGetIntFromDatasetAndRemove(element, variable, defaultValue)
{
    if(variable in element.dataset)
    {
        const value = element.dataset[variable];
        delete element.dataset[variable];
        return parseInt(value);
    }
    return defaultValue;
};
export function xGetIntListFromDatasetAndRemove(element, variable, defaultValue)
{
    if(variable in element.dataset)
    {
        let value = element.dataset[variable];
        delete element.dataset[variable];
        value = value.split(',');
        for(let i = 0; i < value.length; i++)
        {
            value[i] = parseInt(value[i]);
        }
        return value;
    }
    return defaultValue;
};
export function xGetFloatFromDatasetAndRemove(element, variable, defaultValue)
{
    if(variable in element.dataset)
    {
        const value = element.dataset[variable];
        delete element.dataset[variable];
        return parseFloat(value);
    }
    return defaultValue;
};
export function xGetFloatListFromDatasetAndRemove(element, variable, defaultValue)
{
    if(variable in element.dataset)
    {
        let value = element.dataset[variable];
        delete element.dataset[variable];
        value = value.split(',');
        for(let i = 0; i < value.length; i++)
        {
            value[i] = parseFloat(value[i]);
        }
        return value;
    }
    return defaultValue;
};
export function xSetValueInDictionary(dictionary, path, value, overwrite)
{
    overwrite = overwrite !== undefined ? overwrite : true;
    const pathFront = path[0];
    const pathRemaining = path.slice(1);
    if(pathRemaining.length == 0)
    {
        if(overwrite)
        {
            dictionary[pathFront] = value;
        }
        return dictionary[pathFront];
    }
    if(!(pathFront in dictionary))
    {
        dictionary[pathFront] = {};
    }
    if(XTypeChecks.xIsObject(dictionary[pathFront]))
    {
        return xSetValueInDictionary(dictionary[pathFront], pathRemaining, value, overwrite);
    }
    return null;
};
